import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { updateUser } from '../../Store/user/userSlice';
import { avatarDisplayUrl } from '../../utils/imageUrl';
import { handleAvatarImgError, isPlaceholderAvatar } from '../../utils/defaultAvatar';
import ProfileNavigation from './ProfileNavigation';
import styles from '../../styles/Profile.module.scss';

const Profile = () => {
  const dispatch = useDispatch();
  const { currentUser } = useSelector(({ user }) => user);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [values, setValues] = useState({
    name: '',
    email: '',
    password: '',
    avatar: null,
    avatarPreview: '',
  });

  useEffect(() => {
    if (!currentUser) return;
    setValues({
      name: currentUser.name || '',
      email: currentUser.email || '',
      password: '',
      avatar: null,
      avatarPreview: currentUser.avatar || '',
    });
  }, [currentUser?.id]);

  useEffect(() => {
    document.title = 'Редагування профілю - Інтернет-магазин';
    return () => {
      document.title = 'Інтернет-магазин';
    };
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === 'avatar' && files?.[0]) {
      const file = files[0];
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'Розмір зображення не повинен перевищувати 5 МБ' });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setValues((prev) => ({
          ...prev,
          avatar: file,
          avatarPreview: reader.result,
        }));
      };
      reader.readAsDataURL(file);
      return;
    }

    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!values.name || !values.email) {
      setMessage({ type: 'error', text: "Ім'я та email обов'язкові для заповнення" });
      return;
    }

    if (values.password && values.password.length < 6) {
      setMessage({ type: 'error', text: 'Пароль повинен містити мінімум 6 символів' });
      return;
    }

    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('email', values.email);
      if (values.password) formData.append('password', values.password);

      const avatarInput = e.currentTarget.elements.namedItem('avatar');
      const avatarFile = avatarInput?.files?.[0] ?? null;
      const previousAvatar = currentUser?.avatar || '';
      const hadNewAvatar = Boolean(avatarFile);

      if (values.avatarPreview?.startsWith('data:') && !avatarFile) {
        setMessage({ type: 'error', text: 'Файл аватара втрачено. Оберіть зображення знову.' });
        setIsLoading(false);
        return;
      }

      if (avatarFile) {
        formData.append('avatar', avatarFile, avatarFile.name);
      }

      const updatedUser = await dispatch(updateUser(formData)).unwrap();

      if (hadNewAvatar) {
        const newAvatar = updatedUser?.avatar || '';
        if (!newAvatar || newAvatar === previousAvatar || isPlaceholderAvatar(newAvatar)) {
          setMessage({ type: 'error', text: 'Аватар не збережено в базі. Перевірте права на php-api/storage та BASE_URL у .env' });
          setIsLoading(false);
          return;
        }
      }
      setMessage({ type: 'success', text: 'Профіль успішно оновлено' });
      setValues((prev) => ({
        ...prev,
        password: '',
        avatar: null,
        avatarPreview: updatedUser.avatar || prev.avatarPreview,
      }));
    } catch (error) {
      setMessage({
        type: 'error',
        text: typeof error === 'string' ? error : 'Помилка при оновленні профілю',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!currentUser) {
    return (
      <section className={styles.profile}>
        <div className={styles.empty}>
          <h2>Увійдіть в кабінет</h2>
          <p>Для редагування профілю необхідно увійти в свій кабінет покупця, або зареєструватися</p>
          <Link to="/" className={styles.link}>
            Повернутися на головну
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.profile}>
      <ProfileNavigation />
      <div className={styles.contentWrapper}>
        <div className={styles.profileForm}>
          <h2 className={styles.profileTitle}>Редагування профілю</h2>

          {message.text && (
            <div
              className={message.type === 'error' ? styles.error : styles.success}
              role="alert"
            >
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className={styles.formGroup}>
              <label htmlFor="avatar">Аватар</label>
              <div className={styles.avatarSection}>
                <div className={styles.avatarPreview}>
                  <img
                    src={
                      values.avatarPreview?.startsWith('data:')
                        ? values.avatarPreview
                        : avatarDisplayUrl(values.avatarPreview)
                    }
                    alt="Аватар"
                    className={styles.avatarImage}
                    onError={handleAvatarImgError}
                  />
                </div>
                <input
                  type="file"
                  id="avatar"
                  name="avatar"
                  accept="image/*"
                  onChange={handleChange}
                  className={styles.fileInput}
                />
                <label htmlFor="avatar" className={styles.fileLabel}>
                  Обрати зображення
                </label>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="name">Ім'я</label>
              <input
                type="text"
                id="name"
                name="name"
                value={values.name}
                onChange={handleChange}
                placeholder="Введіть ім'я"
                required
                className={styles.formInput}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={values.email}
                onChange={handleChange}
                placeholder="Введіть email"
                required
                className={styles.formInput}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="password">
                Новий пароль (залиште порожнім, якщо не хочете змінювати)
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={values.password}
                onChange={handleChange}
                placeholder="Введіть новий пароль"
                className={styles.formInput}
              />
            </div>

            <div className={styles.formActions}>
              <button type="submit" className={styles.submitButton} disabled={isLoading}>
                {isLoading ? 'Збереження...' : 'Оновити'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Profile;
